import React, { useState, useEffect } from "react";
import axios from "axios";
import { ChatCircleDots, X, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UncleDaveWidget() {
  const { t, isRtl } = useLang();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Prefill email if user is logged in
  useEffect(() => {
    if (!open) return;
    try {
      const token = localStorage.getItem("esg_token");
      if (!token) return;
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (r.data?.email && !email) setEmail(r.data.email); })
        .catch(() => {});
    } catch {}
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/support/contact`, {
        email,
        message,
        page: window.location.pathname,
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t("uncledave_error"));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setSent(false);
      setMessage("");
      setError("");
    }, 250);
  };

  const sideStyle = isRtl ? { left: 24, right: "auto" } : { right: 24, left: "auto" };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="uncledave-open"
          aria-label={t("uncledave_open")}
          className="fixed bottom-6 z-[60] flex items-center gap-2 bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold text-sm py-3 px-4 sm:px-5 rounded-full shadow-2xl shadow-[#01AF9B]/30 transition-all hover:scale-[1.03]"
          style={sideStyle}
        >
          <ChatCircleDots size={20} weight="fill" />
          <span className="hidden sm:inline">{t("uncledave_open")}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="uncledave-panel"
          className="fixed bottom-6 z-[60] w-[calc(100vw-32px)] sm:w-[380px] max-h-[80vh] bg-[#0F1115] border border-slate-800/80 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-200"
          style={{ ...sideStyle, fontFamily: "Outfit, sans-serif" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#01AF9B] to-[#018F7E] px-5 py-4 flex items-start justify-between gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-white text-base leading-tight">{t("uncledave_greeting")}</h3>
              {!sent && <p className="text-white/85 text-xs mt-1 leading-snug">{t("uncledave_subtitle")}</p>}
            </div>
            <button onClick={reset} data-testid="uncledave-close"
              className="text-white/80 hover:text-white p-1 -mr-1 transition-colors">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 overflow-y-auto">
            {sent ? (
              <div className="text-center py-3" data-testid="uncledave-success">
                <CheckCircle size={48} weight="fill" className="text-[#01AF9B] mx-auto mb-3" />
                <h4 className="font-bold text-white text-base mb-2">{t("uncledave_success_title")}</h4>
                <p className="text-slate-300 text-sm leading-relaxed mb-5">{t("uncledave_success_body")}</p>
                <button onClick={reset} data-testid="uncledave-success-close"
                  className="text-sm font-semibold text-[#01AF9B] hover:text-white transition-colors">
                  {t("uncledave_close")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                    {t("uncledave_email_label")}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("uncledave_email_placeholder")}
                    required
                    data-testid="uncledave-email"
                    className="w-full bg-[#070709] border border-slate-700/80 focus:border-[#01AF9B] rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                    {t("uncledave_msg_label")}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t("uncledave_msg_placeholder")}
                    required
                    rows={4}
                    maxLength={5000}
                    data-testid="uncledave-message"
                    className="w-full bg-[#070709] border border-slate-700/80 focus:border-[#01AF9B] rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 outline-none transition-colors resize-none"
                  />
                </div>
                {error && (
                  <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="uncledave-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="uncledave-submit"
                  className="w-full bg-[#01AF9B] hover:bg-[#018F7E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                  {busy ? t("uncledave_sending") : t("uncledave_send")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
